<script setup lang="ts">
import HotIcon from '@/assets/icons/MenuFilters/HotIcon.vue';
import InputNumber from '@/components/BaseComponents/InputNumber.vue';
import { useProfileRestaurant } from '@/composables/useProfileRestaurant.js';
import type { MessageSchema } from '@/i18n/index.js';
import type { AttributesList, Restaurant } from '@/types/restaurants.js';
import { useClipboard, useDebounceFn } from '@vueuse/core';
import InputText from 'primevue/inputtext';
import RadioButton from 'primevue/radiobutton';
import Textarea from 'primevue/textarea';
import { useToast } from 'primevue/usetoast';
import { computed, markRaw, onMounted, ref, watch, watchEffect, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
// import { useRouter } from 'vue-router';
import ProfileMenuItem from '../ProfileMenuItem.vue';
import TheRestaurantCountries from './TheRestaurantCountries.vue';
// import SupplierIcon from '@/assets/icons/Profile/SupplierIcon.vue'
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
// import CountrySelect from './CountrySelect.vue'
import TheWorkingPeriodAllowYesNo from '@/components/Manager/TheWorkingPeriodAllowYesNo.vue';
import { useFetch } from '@/composables/useFetch';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { formatTimeRange } from '@/utils/time';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import CountrySelect from './CountrySelect.vue';

const props = defineProps<{
  restaurant: Restaurant;
  attributes: AttributesList;
}>();
const emit = defineEmits<{
  (e: 'updateRestaurant', restaurant: Restaurant): void;
  (e: 'goToTooling'): void;
}>();

const { t, te } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { copy } = useClipboard();
const toast = useToast();

function handleCopy(gastroId: string) {
  copy(gastroId);
  toast.add({
    severity: 'success',
    summary: t('common.success'),
    detail: t('profile.profile.profileTab.personnalData.gastroIdCopied'),
    life: 3000,
  });
}

// Remove fetchData here as it's now used within the composable for this specific logic
// const { fetchData } = useFetch();

// const router = useRouter();

const restaurantName = ref(props.restaurant.name);
// const restaurantWebsiteUrl = ref(props.restaurant.website_url)
const restaurantWebsiteUrl = ref(stripProtocol(props.restaurant.website_url));
const restaurantPhoneNumber = ref(props.restaurant.phone_number);
// const restaurantLogoUrl = ref(props.restaurant.logo_url);
const restaurantDescription = ref(props.restaurant.description);
const restaurationType = ref(props.restaurant.attributes.restaurationType[0].id);
const restaurantCoverCount = ref(props.restaurant.cover_count);
const restaurantCooks = ref(props.restaurant.cooks_per_service);
// const selectedCountry = ref(props.restaurant.country_alpha2_code)
const selectedCountry = ref((props.restaurant.country_alpha2_code || '').toUpperCase());
// const selectedCountry = ref(props.restaurant.country_alpha2_code || '')
const billingEmail = ref(props.restaurant.billing_email ?? '');
const addressLine1 = ref(props.restaurant.address?.address_line_1 ?? '');
const addressCity = ref(props.restaurant.address?.city?.name ?? '');
const addressPostalCode = ref(props.restaurant.address?.city?.postal_code ?? '');

// Shift Slot Editing
const editingShiftSlotId = ref<number | null>(null);
const tempShiftSlotName = ref(''); // Added for editing slot name
const tempShiftSlotStartTime = ref('');
const tempShiftSlotEndTime = ref('');
const isUpdatingShiftSlot = ref<Record<number, boolean>>({});

const employeeStore = useEmployeeStore();

// Destructure the new function along with existing ones
const { updateRestaurantProperty, updateRestaurantAttributes, postRestaurantAddress, updateRestaurantShiftSlot } = useProfileRestaurant(
  toast,
  props.restaurant.id!,
);

// Helper function to get the display name (translated or raw)
const getShiftSlotDisplayName = (slotName: string) => {
  const translationKey = `employee.slots.timeSlots.${slotName}`;
  return te(translationKey) ? t(translationKey) : slotName;
};

// Assume useProfileRestaurant is extended with updateRestaurantShiftSlot
// const { updateRestaurantProperty, updateRestaurantAttributes, postRestaurantAddress, updateRestaurantShiftSlot } = useProfileRestaurant(toast, props.restaurant.id!);
// If updateRestaurantShiftSlot is not available, you'll need to implement it.
// For this example, we'll assume it exists and updates the store/props reactively.

const showAddressDialog = ref(false);
const tempAddressLine1 = ref(addressLine1.value);
const tempAddressCity = ref(addressCity.value);
const tempAddressPostalCode: Ref<string> = ref(addressPostalCode.value);

function openAddressDialog() {
  tempAddressLine1.value = addressLine1.value;
  tempAddressCity.value = addressCity.value;
  tempAddressPostalCode.value = addressPostalCode.value;
  showAddressDialog.value = true;
}

async function validateAddressDialog() {
  addressLine1.value = tempAddressLine1.value;
  addressCity.value = tempAddressCity.value;
  addressPostalCode.value = tempAddressPostalCode.value;
  await postRestaurantAddress({
    address_line_1: addressLine1.value,
    city: addressCity.value,
    postal_code: addressPostalCode.value,
  });
  showAddressDialog.value = false;
}

function cancelAddressDialog() {
  showAddressDialog.value = false;
}

const tempBillingEmail = ref(billingEmail.value);
const showBillingEmailDialog = ref(false);

function openBillingEmailDialog() {
  tempBillingEmail.value = billingEmail.value;
  showBillingEmailDialog.value = true;
}

async function validateBillingEmailDialog() {
  billingEmail.value = tempBillingEmail.value;
  await updateBillingEmail();
  showBillingEmailDialog.value = false;
}

function cancelBillingEmailDialog() {
  showBillingEmailDialog.value = false;
}

// --- New functions for Shift Slot Editing ---
function startEditingShiftSlot(slot: { id: number; name: string; start_time?: string; end_time?: string }) {
  editingShiftSlotId.value = slot.id;
  tempShiftSlotName.value = getShiftSlotDisplayName(slot.name); // Use translated name for editing
  tempShiftSlotStartTime.value = slot.start_time || '';
  tempShiftSlotEndTime.value = slot.end_time || '';
}

function cancelEditingShiftSlot() {
  editingShiftSlotId.value = null;
  tempShiftSlotName.value = ''; // Clear temporary name
  tempShiftSlotStartTime.value = '';
  tempShiftSlotEndTime.value = '';
}

async function updateShiftSlotTime(slotId: number) {
  if (!props.restaurant.id) return;

  isUpdatingShiftSlot.value[slotId] = true;
  try {
    // Call the composable function
    const success = await updateRestaurantShiftSlot(slotId, {
      name: tempShiftSlotName.value,
      start_time: tempShiftSlotStartTime.value || null,
      end_time: tempShiftSlotEndTime.value || null,
    });

    if (success) {
      const targetSlot = employeeStore.shiftSlots?.find((s) => s.id === slotId);
      if (targetSlot) {
        targetSlot.name = tempShiftSlotName.value;
        targetSlot.start_time = tempShiftSlotStartTime.value ? tempShiftSlotStartTime.value : undefined;
        targetSlot.end_time = tempShiftSlotEndTime.value ? tempShiftSlotEndTime.value : undefined;
      }
      // Toast is now handled by the composable, but you might want to keep specific UI updates here
      cancelEditingShiftSlot();
    }
    // Error toast is also handled by the composable
  } catch (error) {
    // This catch block can be simplified or removed if the composable handles all error scenarios and toasting.
    // Kept for now in case of unexpected errors not caught by the composable.
    console.error('Error updating shift slot in component:', error);
    // Optionally, add a fallback toast here if needed, though the composable should cover it.
    // toast.add({ severity: 'error', summary: t('common.error'), detail: t('profile.profile.restaurantTab.shiftSlots.updateError'), life: 3000 });
  } finally {
    isUpdatingShiftSlot.value[slotId] = false;
  }
}

function isEditingShiftSlot(slotId: number): boolean {
  return editingShiftSlotId.value === slotId;
}
// --- End of new functions ---

// const { updateRestaurantProperty, updateRestaurantAttributes, postRestaurantAddress } = useProfileRestaurant(toast, props.restaurant.id!);

async function updateProperty(propertyName: string, value: string | number) {
  // Skip update if value is empty for required fields like 'name'
  if (propertyName === 'name' && typeof value === 'string' && value.trim() === '') {
    return;
  }
  const updateResponse = await updateRestaurantProperty(propertyName, value);
  if (updateResponse !== null) emit('updateRestaurant', updateResponse);
}

// Create a debounced version of updateProperty
const updatePropertyDebounced = useDebounceFn((property: string, value: string | number) => {
  updateProperty(property, value);
}, 500);

watch(restaurationType, async () => {
  const updateResponse = await updateRestaurantAttributes({
    attributeIds: [restaurationType.value],
    isRestaurationType: true,
  });
  if (updateResponse !== null) emit('updateRestaurant', updateResponse);
});

watchEffect(() => {
  if (restaurantCoverCount.value === null) restaurantCoverCount.value = 1;
  if (restaurantCooks.value === null) restaurantCooks.value = 1;
});

function stripProtocol(url: string | null | undefined): string {
  if (!url) return '';
  return url.replace(/^(https?:\/\/)/, '');
}

function formatWebsiteUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
}

async function updateWebsiteUrl() {
  const formattedUrl = formatWebsiteUrl(restaurantWebsiteUrl.value);
  await updateProperty('website_url', formattedUrl);
}

async function updateBillingEmail() {
  const updateResponse = await updateRestaurantProperty('billing_email', billingEmail.value);
  if (updateResponse !== null) emit('updateRestaurant', updateResponse);
}

watch(restaurantWebsiteUrl, (newValue) => {
  restaurantWebsiteUrl.value = stripProtocol(newValue);
});

watch(selectedCountry, async (newValue) => {
  if (!newValue) return;
  await updateProperty('country_alpha2_code', newValue.toUpperCase());
});

// const filteredShiftSlots = computed(() => {
//   return employeeStore.shiftSlots.filter((slot) => !slot.is_default);
// });

// Update the computed to group by department
const shiftSlotsByDepartment = computed(() => {
  const grouped: Record<string, typeof employeeStore.shiftSlots> = {};

  const nonDefaultSlots = employeeStore.shiftSlots.filter((slot) => !slot.is_default);

  nonDefaultSlots.forEach((slot) => {
    if (slot.department) {
      const departmentName = slot.department.name;
      if (!grouped[departmentName]) {
        grouped[departmentName] = [];
      }
      grouped[departmentName].push(slot);
    } else {
      // Handle slots without department (fallback)
      if (!grouped['no_department']) {
        grouped['no_department'] = [];
      }
      grouped['no_department'].push(slot);
    }
  });

  // Sort slots within each department by start_time (earliest first)
  Object.values(grouped).forEach((slots) => {
    slots.sort((a, b) => {
      // If both have start_time, compare them
      if (a.start_time && b.start_time) {
        return a.start_time.localeCompare(b.start_time);
      }
      // Slots with start_time come before those without
      if (a.start_time && !b.start_time) return -1;
      if (!a.start_time && b.start_time) return 1;
      // If neither has start_time, fall back to order
      return (a.order || 0) - (b.order || 0);
    });
  });

  return grouped;
});

// Get department display name
function getDepartmentDisplayName(departmentName: string): string {
  if (departmentName === 'no_department') {
    return t('profile.profile.restaurantTab.shiftSlots.noDepartment');
  }
  return t(`employee.kitchenPositions.departments.${departmentName}`);
}

const allowEmployeeEdits = ref(props.restaurant.allow_employee_working_period_edits ?? true);
const isUpdatingSettings = ref(false);

// Options for the SelectButton
// const workingPeriodOptions = ref([
//   {
//     label: computed(() => t('profile.profile.restaurantTab.settings.workingPeriods.allowOption')),
//     value: true,
//     icon: 'pi pi-check-circle'
//   },
//   {
//     label: computed(() => t('profile.profile.restaurantTab.settings.workingPeriods.restrictOption')),
//     value: false,
//     icon: 'pi pi-times-circle'
//   }
// ]);

// Add this method
async function updateEmployeeEditSettings() {
  if (!props.restaurant.id) return;

  isUpdatingSettings.value = true;
  try {
    const { data, error } = await fetchData(`${useEnvStore().apiUrl}/restaurants/${props.restaurant.id}/settings/working-periods`, 'PUT', {
      allow_employee_working_period_edits: allowEmployeeEdits.value,
    });
    if (data) {
      console.log('Settings updated successfully');
    }

    if (error) {
      console.error('Error updating settings:', error);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('profile.profile.restaurantTab.settings.updateError'),
        life: 3000,
      });
      // Revert the toggle
      allowEmployeeEdits.value = !allowEmployeeEdits.value;
    } else {
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('profile.profile.restaurantTab.settings.updateSuccess'),
        life: 3000,
      });
    }
  } catch (error) {
    console.error('Error updating settings:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('profile.profile.restaurantTab.settings.updateError'),
      life: 3000,
    });
    // Revert the toggle
    allowEmployeeEdits.value = !allowEmployeeEdits.value;
  } finally {
    isUpdatingSettings.value = false;
  }
}

// Add fetchData composable if not already imported
const { fetchData } = useFetch();

onMounted(async () => {
  // Initialize restaurant data
  await employeeStore.fetchShiftSlotsIfNeeded();
});
</script>

<template>
  <div class="flex flex-col gap-8">
    <!-- Section: General Information -->
    <div class="rounded-lg border p-4">
      <h3>{{ t('profile.profile.restaurantTab.generalInfo') }}</h3>
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <p class="text-sm">{{ t('profile.profile.restaurantTab.historyAndData.description') }}</p>
        <div>
          <h4 class="mt-2">{{ t('profile.profile.restaurantTab.historyAndData.restaurantName') }}</h4>
          <InputText v-model="restaurantName" class="w-full" @input="updatePropertyDebounced('name', restaurantName)" />
        </div>
        <div>
          <h4 class="mt-2">
            {{ t('profile.profile.restaurantTab.historyAndData.restaurantDescription') }}
          </h4>
          <Textarea
            v-model="restaurantDescription"
            class="w-full"
            auto-resize
            :rows="3"
            @input="updatePropertyDebounced('description', restaurantDescription)"
          />
        </div>
      </div>
      <p class="mt-4 hover:cursor-pointer" @click="handleCopy(restaurant.gastro_id)">
        {{ t('profile.profile.profileTab.personnalData.gastroId') }} :
        <span>
          <b>{{ restaurant.gastro_id }}</b>
        </span>
        <i class="pi pi-copy ml-1 text-xl" />
      </p>
    </div>

    <!-- Section: Contact & Location -->
    <div class="rounded-lg border p-4">
      <h3 class="mb-2">{{ t('profile.profile.restaurantTab.contactAndLocation') }}</h3>
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <h4 class="mt-2">
            {{ t('profile.profile.restaurantTab.historyAndData.billingEmail') }}
          </h4>
          <div class="flex flex-row items-center justify-between">
            <p>
              {{ billingEmail || t('profile.profile.restaurantTab.historyAndData.billingEmail') }}
            </p>
            <Button class="ml-4" icon="pi pi-pencil" @click="openBillingEmailDialog" />
          </div>
          <Dialog
            v-model:visible="showBillingEmailDialog"
            modal
            :header="t('profile.profile.restaurantTab.historyAndData.billingEmail')"
            :style="{ width: '25rem', maxWidth: '90vw' }"
          >
            <div class="flex flex-col gap-2">
              <InputText
                v-model="tempBillingEmail"
                class="w-full"
                :placeholder="t('profile.profile.restaurantTab.historyAndData.billingEmail')"
                type="email"
              />
            </div>
            <template #footer>
              <Button :label="t('common.cancel')" outlined @click="cancelBillingEmailDialog" />
              <Button :label="t('common.validate')" @click="validateBillingEmailDialog" />
            </template>
          </Dialog>
        </div>
        <div>
          <h4 class="mt-2">{{ t('profile.profile.restaurantTab.historyAndData.restaurantAdress') }}</h4>
          <div class="flex flex-row items-center justify-between">
            <p>
              {{
                addressLine1 || addressCity || addressPostalCode
                  ? `${addressLine1}, ${addressPostalCode} ${addressCity}`
                  : t('profile.profile.restaurantTab.historyAndData.noAddress')
              }}
            </p>
            <Button class="ml-4" icon="pi pi-pencil" @click="openAddressDialog" />
          </div>
          <Dialog
            v-model:visible="showAddressDialog"
            modal
            :header="t('profile.profile.restaurantTab.historyAndData.restaurantAdress')"
            :style="{ width: '25rem', maxWidth: '90vw' }"
          >
            <div class="flex flex-col gap-2">
              <InputText
                v-model="tempAddressLine1"
                class="w-full"
                :placeholder="t('profile.profile.restaurantTab.historyAndData.addressPlaceholder')"
              />
              <div class="flex gap-2">
                <InputText
                  v-model="tempAddressPostalCode"
                  type="number"
                  class="w-1/3"
                  :placeholder="t('profile.profile.restaurantTab.historyAndData.postalCodePlaceholder')"
                />
                <InputText v-model="tempAddressCity" class="w-2/3" :placeholder="t('profile.profile.restaurantTab.historyAndData.cityPlaceholder')" />
              </div>
            </div>
            <template #footer>
              <Button :label="t('common.cancel')" outlined @click="cancelAddressDialog" />
              <Button :label="t('common.validate')" @click="validateAddressDialog" />
            </template>
          </Dialog>
          <div>
            <h4 class="mt-2">
              {{ t('profile.profile.restaurantTab.historyAndData.countryPlaceholder') }}
            </h4>
            <CountrySelect v-model="selectedCountry" class="w-full md:w-1/2" />
          </div>
        </div>
        <div>
          <h4 class="mt-2">
            {{ t('profile.profile.restaurantTab.historyAndData.restaurantWebsite') }}
          </h4>
          <div class="flex flex-row items-center gap-2">
            <p>https://</p>
            <InputText v-model="restaurantWebsiteUrl" class="w-full" placeholder="www.example.com" @blur="updateWebsiteUrl" />
          </div>
        </div>
        <div>
          <h4 class="mt-2">
            {{ t('profile.profile.restaurantTab.historyAndData.restaurantPhoneNb') }}
          </h4>
          <InputText v-model="restaurantPhoneNumber" class="w-full" @blur="updateProperty('phone_number', restaurantPhoneNumber)" />
        </div>
      </div>
    </div>

    <!-- Section: Operational Details -->
    <div class="rounded-lg border p-4">
      <h3 class="mb-2">{{ t('profile.profile.restaurantTab.operationalDetails') }}</h3>
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div class="flex flex-row items-end justify-between">
          <div>
            <h4>{{ t('profile.profile.restaurantTab.servings.title') }}</h4>
            <p class="mr-2 text-sm">{{ t('profile.profile.restaurantTab.servings.description') }}</p>
          </div>
          <InputNumber
            v-model="restaurantCoverCount"
            class="w-24 shrink-0"
            :min="1"
            @blur="
              () => {
                if (restaurantCoverCount < 1) restaurantCoverCount = 1;
                updateProperty('cover_count', restaurantCoverCount > 0 ? restaurantCoverCount : 1);
              }
            "
          />
        </div>
        <div class="flex flex-row items-end justify-between">
          <div>
            <h4 class="mr-2">{{ t('profile.profile.restaurantTab.cooks.title') }}</h4>
            <p class="mr-2 text-sm">{{ t('profile.profile.restaurantTab.cooks.description') }}</p>
          </div>
          <InputNumber
            v-model="restaurantCooks"
            class="w-24 shrink-0"
            :min="1"
            @blur="
              () => {
                if (restaurantCooks < 1) restaurantCooks = 1;
                updateProperty('cooks_per_service', restaurantCooks > 0 ? restaurantCooks : 1);
              }
            "
          />
        </div>
        <div v-if="useUserStore().user?.restaurants[0].role === 'admin' || useUserStore().user?.restaurants[0].role === 'manager'" class="mt-6">
          <h4 class="mb-3">{{ t('profile.profile.restaurantTab.settings.workingPeriods.title') }}</h4>

          <div class="space-y-4">
            <div>
              <label class="mb-2 block text-sm font-medium">
                {{ t('profile.profile.restaurantTab.settings.workingPeriods.allowEmployeeEdits') }}
              </label>
              <p class="mb-3 text-xs text-gray-600">
                {{ t('profile.profile.restaurantTab.settings.workingPeriods.allowEmployeeEditsDescription') }}
              </p>

              <!-- SelectButton with both options visible -->
              <div class="flex items-center justify-center">
                <!-- <SelectButton v-model="allowEmployeeEdits" :options="workingPeriodOptions" option-label="label"
                  option-value="value" :disabled="isUpdatingSettings" @change="updateEmployeeEditSettings">
                  <template #option="{ option }">
                    <div class="flex items-center gap-2 px-3 py-2">
                      <i
                        :class="[option.icon, allowEmployeeEdits === option.value ? 'text-black' : 'text-black-500']"></i>
                      <span>{{ option.label.value }}</span>
                    </div>
                  </template>
                </SelectButton> -->
                <!-- <SelectButton v-model="allowEmployeeEdits" :options="workingPeriodOptions" option-label="label"
                  option-value="value" :disabled="isUpdatingSettings" @change="updateEmployeeEditSettings">
                  <template #option="{ option }">
                    <div class="flex items-center gap-2 px-3 py-2" :class="{
                      'bg-green-100 text-green-700 border-green-300': allowEmployeeEdits === option.value && option.value === true,
                      'bg-red-100 text-red-700 border-red-300': allowEmployeeEdits === option.value && option.value === false,
                      'text-gray-600': allowEmployeeEdits !== option.value
                    }">
                      <i :class="[
                        option.icon,
                        allowEmployeeEdits === option.value && option.value === true ? 'text-green-600' :
                          allowEmployeeEdits === option.value && option.value === false ? 'text-red-600' :
                            'text-gray-500'
                      ]"></i>
                      <span>{{ option.label.value }}</span>
                    </div>
                  </template>
                </SelectButton> -->
                <div class="flex items-center justify-center">
                  <TheWorkingPeriodAllowYesNo
                    v-model="allowEmployeeEdits"
                    :disabled="isUpdatingSettings"
                    @update:model-value="updateEmployeeEditSettings"
                  />
                </div>
              </div>

              <!-- Loading indicator -->
              <!-- <div v-if="isUpdatingSettings" class="mt-2 flex items-center gap-2 text-sm text-black-500">
              <i class="pi pi-spin pi-spinner"></i>
              {{ t('profile.profile.restaurantTab.settings.updating') }}
            </div> -->
            </div>
          </div>
        </div>

        <!-- Shift Slots Section - Updated to show departments -->
        <div class="md:col-span-2">
          <div class="flex flex-col gap-4">
            <div>
              <h4>{{ t('profile.profile.restaurantTab.shiftSlots.title') }}</h4>
            </div>

            <!-- Check if we have any shift slots -->
            <div v-if="Object.keys(shiftSlotsByDepartment).length > 0" class="flex flex-col gap-6">
              <!-- Loop through each department -->
              <div v-for="(slots, departmentName) in shiftSlotsByDepartment" :key="departmentName" class="flex flex-col gap-3">
                <!-- Department Title -->
                <div class="flex items-center gap-2 border-b pb-2">
                  <h5 class="font-semibold text-sm">{{ getDepartmentDisplayName(departmentName) }}</h5>
                </div>

                <!-- Shift Slots for this department -->
                <div class="flex flex-col gap-3 pl-4">
                  <div v-for="slot in slots" :key="slot.id" class="rounded-lg border p-3 shadow-sm">
                    <div v-if="!isEditingShiftSlot(slot.id)" class="flex items-center justify-between">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium">{{ getShiftSlotDisplayName(slot.name) }}</span>
                        <span class="text-xs text-gray-400">•</span>
                        <span class="text-sm text-gray-500">
                          {{
                            slot.start_time || slot.end_time
                              ? formatTimeRange(slot.start_time, slot.end_time, t)
                              : t('profile.profile.restaurantTab.shiftSlots.noTimeSet')
                          }}
                        </span>
                      </div>
                      <Button icon="pi pi-pencil" text size="small" @click="startEditingShiftSlot(slot)" />
                    </div>
                    <div v-else class="flex flex-col gap-3">
                      <!-- Editing state container -->
                      <div class="flex flex-col gap-1">
                        <!-- Slot Name -->
                        <label :for="`input-slot-name-${slot.id}`" class="text-xs font-medium">
                          {{ t('profile.profile.restaurantTab.shiftSlots.shiftName') }}:
                        </label>
                        <InputText :id="`input-slot-name-${slot.id}`" v-model="tempShiftSlotName" type="text" size="small" class="w-full text-xs" />
                      </div>
                      <div class="flex gap-2">
                        <!-- Start and End Time side-by-side -->
                        <div class="flex flex-grow flex-col gap-1">
                          <label :for="`input-slot-start-time-${slot.id}`" class="text-xs font-medium">{{ t('employee.slots.startTime') }}:</label>
                          <InputText
                            :id="`input-slot-start-time-${slot.id}`"
                            v-model="tempShiftSlotStartTime"
                            type="time"
                            size="small"
                            class="w-full text-xs"
                          />
                        </div>
                        <div class="flex flex-grow flex-col gap-1">
                          <label :for="`input-slot-end-time-${slot.id}`" class="text-xs font-medium">{{ t('employee.slots.endTime') }}:</label>
                          <InputText
                            :id="`input-slot-end-time-${slot.id}`"
                            v-model="tempShiftSlotEndTime"
                            type="time"
                            size="small"
                            class="w-full text-xs"
                          />
                        </div>
                      </div>
                      <div class="mt-2 flex flex-row items-center justify-end gap-2">
                        <!-- Buttons -->
                        <Button
                          :label="t('common.cancel')"
                          icon="pi pi-times"
                          size="small"
                          text
                          severity="secondary"
                          @click="cancelEditingShiftSlot()"
                        />
                        <Button
                          :label="t('common.save')"
                          icon="pi pi-check"
                          size="small"
                          severity="success"
                          :loading="isUpdatingShiftSlot[slot.id]"
                          @click="updateShiftSlotTime(slot.id)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- No slots configured -->
            <div v-else>
              <p class="text-sm text-gray-500">{{ t('profile.profile.restaurantTab.shiftSlots.noSlotsConfigured') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section: Restaurant Type & Inspirations -->
    <div class="rounded-lg border p-4">
      <h3 class="mb-2">{{ t('profile.profile.restaurantTab.typeAndInspirations') }}</h3>
      <div>
        <h4>{{ t('profile.profile.restaurantTab.restaurantionType.title') }}</h4>
        <p class="text-sm">{{ t('profile.profile.restaurantTab.restaurantionType.description') }}</p>
        <div class="mt-2 flex flex-col gap-2">
          <div v-for="attribute in attributes.restaurationType" :key="attribute.id" class="flex flex-row items-center">
            <RadioButton v-model="restaurationType" :input-id="'radio-' + attribute.name" :name="attribute.name" :value="attribute.id" />
            <label :for="'radio-' + attribute.name" class="ml-2">
              {{ t(`profile.profile.restaurantTab.restaurantionType.${attribute.name}`) }}
            </label>
          </div>
        </div>
      </div>
      <div class="mt-6">
        <TheRestaurantCountries
          :attributes="props.attributes.inspirations"
          :restaurant-attributes="props.restaurant.attributes.inspirations"
          @update-countries="updateRestaurantAttributes({ attributeIds: [$event] })"
        />
      </div>
    </div>

    <!-- Section: Other -->
    <div class="rounded-lg border p-4">
      <h3 class="mb-2">{{ t('profile.profile.restaurantTab.other.title') }}</h3>
      <ProfileMenuItem :icon="markRaw(HotIcon)" :name="t('profile.profile.restaurantTab.other.tooling')" :index="0" @click="emit('goToTooling')" />

      <!-- Working Period Settings -->

      <div v-if="useEnvStore().environment == 'local' || useEnvStore().environment !== 'production'" class="mt-4">
        <h4 class="mt-2">Credits</h4>
        {{ useUserStore().user?.restaurants[0].total_credits }}
      </div>
    </div>
  </div>
</template>
